import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import { Layout } from "@/components/supabase/layout";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Turnstile,
  TURNSTILE_SITE_KEY,
} from "@/components/atomic-crm/login/Turnstile";

interface FormData {
  email: string;
}

export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [, { mutateAsync: resetPassword }] = useResetPassword({
    onSuccess: () => {
      redirect("/login?passwordRecoveryEmailSent=1");
    },
    onError: () => undefined,
  });

  const submit = async (values: FormData) => {
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      notify("crm.auth.captcha_required", {
        type: "warning",
        messageArgs: {
          _: "Por favor, complete a verificação do CAPTCHA.",
        },
      });
      return;
    }

    try {
      setLoading(true);
      const params: { email: string; captchaToken?: string } = {
        email: values.email,
      };
      if (TURNSTILE_SITE_KEY && captchaToken) {
        params.captchaToken = captchaToken;
      }
      await resetPassword(params);
    } catch {
      setCaptchaToken(null);
      // Redirect anyway or notify generic message so email existence isn't revealed
      redirect("/login?passwordRecoveryEmailSent=1");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.reset_password.forgot_password", {
            _: "Forgot password?",
          })}
        </h1>
        <p>
          {translate("ra-supabase.reset_password.forgot_password_details", {
            _: "Enter your email to receive a reset password link.",
          })}
        </p>
      </div>
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
      >
        <TextInput
          source="email"
          label={translate("ra.auth.email", {
            _: "Email",
          })}
          autoComplete="email"
          validate={required()}
        />
        <Turnstile
          onSuccess={(token) => setCaptchaToken(token)}
          onExpire={() => setCaptchaToken(null)}
          onError={() => setCaptchaToken(null)}
        />
        <Button
          type="submit"
          className="cursor-pointer"
          disabled={loading || (Boolean(TURNSTILE_SITE_KEY) && !captchaToken)}
        >
          {translate("crm.action.reset_password", {
            _: "Reset password",
          })}
        </Button>
      </Form>
    </Layout>
  );
};

ForgotPasswordPage.path = "forgot-password";
